import { Button, Input, Modal, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AiFillCaretDown, AiOutlineClose, AiOutlineCloseCircle } from 'react-icons/ai';
import { omitNonLayerProperties, useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import PermissionManagerService from '../../../services/FileManager/permissionManagement.service';
import { onSocketEvent } from '../../../services/socket.service';
import Notification from '../../../shared/components/Notification';
import CustomWarning from '../../../shared/components/CustomWarning';
import FolderSelectorTree from '../../../shared/components/FolderSelectorTree';
import { SelectedFolder } from '../../../types/FileManagerTypes';
import { NotificationTypes } from '../../../enums/notificationTypes';
import './SaveLayerModal.scss';

const { Option } = Select;

interface SaveLayerModalProps {
  saveLayerModalVisible: boolean;
  setSaveLayerModalVisible: (value: boolean) => void;
  onSaved?: () => void;
  /** Map the user is currently in — shown first ("Current") in the map step. */
  currentMapId?: number | string;
}

const NO_MAP = -1;
type Step = 'choose' | 'new' | 'existing' | 'map';
type FileFormat = 'shapefile' | 'geojson' | 'kml';

// Maps Layer Properties' sourceFormat enum value to the file-type the
// uploadFeaturesToFolder endpoint understands. The Figma flow doesn't
// expose this — we silently default based on the layer's origin.
const defaultFormatFor = (sourceFormat?: string): FileFormat => {
  const f = (sourceFormat ?? '').toLowerCase();
  if (f.includes('shap')) return 'shapefile';
  if (f.includes('kml')) return 'kml';
  return 'geojson';
};

const SaveLayerModal: React.FC<SaveLayerModalProps> = ({ saveLayerModalVisible, setSaveLayerModalVisible, onSaved, currentMapId }) => {
  const layerProps = useLayerProperitesContext();
  const { createFilemanagerFolder, uploadFeaturesToFolder, getAllMaps, createLayerFromFeatures } = MapPortalService();
  const { getAllUsers, getAllGroups } = PermissionManagerService();

  const { data: allUsers } = useQuery({
    ...getAllUsers(),
    enabled: saveLayerModalVisible,
  });
  const { data: allGroups } = useQuery({
    ...getAllGroups(),
    enabled: saveLayerModalVisible,
  });
  const { mutateAsync: createFolderAsync } = useMutation(createFilemanagerFolder());
  const { mutateAsync: uploadFeaturesAsync, isPending } = useMutation(uploadFeaturesToFolder());
  const { mutateAsync: createLayerFromFeaturesAsync, isPending: addingToMap } = useMutation(createLayerFromFeatures());
  const { data: allMapsData, isLoading: loadingMaps } = useQuery({ ...getAllMaps(), enabled: saveLayerModalVisible });

  const allMaps: any[] = Array.isArray(allMapsData) ? allMapsData : (allMapsData?.maps ?? []);

  // After the file is written, the wizard moves to the map step; this holds the
  // folder it landed in so "add to map" can create the layer from it.
  const [savedFolderId, setSavedFolderId] = useState<string | undefined>();
  const [mapChoice, setMapChoice] = useState<number | undefined>();

  const [step, setStep] = useState<Step>('choose');
  const [locationFolderId, setLocationFolderId] = useState<string | undefined>();
  const [existingFolderId, setExistingFolderId] = useState<string | undefined>();
  const [newFolderName, setNewFolderName] = useState('');
  const [folderSelectorOpen, setFolderSelectorOpen] = useState(false);
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [layerName, setLayerName] = useState('');
  const [permissionValues, setPermissionValues] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Reset the wizard whenever the modal is freshly opened so an aborted
  // previous save doesn't leak its values into the next attempt.
  useEffect(() => {
    if (saveLayerModalVisible) {
      setStep('choose');
      setLocationFolderId(undefined);
      setExistingFolderId(undefined);
      setNewFolderName('');
      setFolderSelectorOpen(false);
      setSelectedFolderName('');
      setLayerName(layerProps?.layerTitle ? `${layerProps.layerTitle} Copy` : '');
      setPermissionValues([]);
      setErrors({});
      setConfirmCloseOpen(false);
      setSavedFolderId(undefined);
      setMapChoice(undefined);
    }
  }, [saveLayerModalVisible, layerProps?.layerTitle]);

  const openFolderSelector = () => setFolderSelectorOpen(true);
  const closeFolderSelector = () => setFolderSelectorOpen(false);

  const handleFolderSelect = (folder: SelectedFolder) => {
    if (step === 'new') setLocationFolderId(folder.id);
    else setExistingFolderId(folder.id);
    setSelectedFolderName(folder.name);
    setErrors((prev) => ({ ...prev, location: '', folder: '' }));
  };

  const permissionOptions = useMemo(
    () => [
      {
        label: 'Users',
        options: (allUsers ?? []).map((u: any) => ({
          label: u.name,
          value: `user-${u.id}`,
        })),
      },
      {
        label: 'Groups',
        options: (allGroups ?? []).map((g: any) => ({
          label: g.name,
          value: `group-${g.id}`,
        })),
      },
    ],
    [allUsers, allGroups],
  );

  const hasDirtyForm = useMemo(() => {
    if (step === 'choose') return false;
    return !!layerName.trim() || !!newFolderName.trim() || !!locationFolderId || !!existingFolderId || permissionValues.length > 0;
  }, [step, layerName, newFolderName, locationFolderId, existingFolderId, permissionValues]);

  const requestClose = () => {
    // On the map step the file is already saved — closing just skips adding to a
    // map, so don't warn about losing work.
    if (step === 'map') {
      setSaveLayerModalVisible(false);
      onSaved?.();
      return;
    }
    if (hasDirtyForm) {
      setConfirmCloseOpen(true);
    } else {
      setSaveLayerModalVisible(false);
    }
  };

  const forceClose = () => {
    setConfirmCloseOpen(false);
    setSaveLayerModalVisible(false);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (step === 'new') {
      if (!locationFolderId) e.location = 'Location is required';
      if (!newFolderName.trim()) e.folderName = 'Folder name is required';
    }
    if (step === 'existing') {
      if (!existingFolderId) e.folder = 'Folder is required';
    }
    if (!layerName.trim()) e.fileName = 'File name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      let targetFolderId = existingFolderId;

      // AC-02 "Save in New Folder": create the folder under the chosen
      // parent location, then write into it.
      if (step === 'new') {
        const created = await createFolderAsync({
          folderName: newFolderName.trim(),
          currentDirectoryId: locationFolderId!,
        });
        targetFolderId = created?.folder?.folder_id ?? created?.folderId ?? created?.id ?? created?.folder?.id;
        if (!targetFolderId) {
          throw new Error('Folder creation succeeded but no folder id returned');
        }
      }

      // AC-05: pass only selected feature gids when an attribute-table
      // selection is active; otherwise leave empty so the backend exports
      // the whole dataset.
      const selectedFeatureIds: string[] = (layerProps?.selectedFeatureIds ?? []).map(String);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedFeatureIds: _drop, ...propertiesToSend } = omitNonLayerProperties(layerProps) as any;

      // Async save: the export now runs on the worker, so we pass the chosen
      // permissions in the payload and the queue completed-handler applies them
      // to each file it creates (the files don't exist yet to set them here).
      const userValues = permissionValues.filter((v) => v.startsWith('user-')).map((v) => v.replace('user-', ''));
      const groupValues = permissionValues.filter((v) => v.startsWith('group-')).map((v) => v.replace('group-', ''));

      const fileType = defaultFormatFor(layerProps?.sourceFormat);
      let stopListening: (() => void) | undefined;
      let queuedJobId: string | number | undefined;
      const datasetReady = new Promise<any>((resolve, reject) => {
        stopListening = onSocketEvent<any>('save-features:completed', (event) => {
          if (queuedJobId && String(event?.jobId) !== String(queuedJobId)) return;
          if (event?.folderId !== targetFolderId) return;
          if (event?.status === 'failed') {
            stopListening?.();
            reject(new Error(event.error || 'Dataset creation failed'));
            return;
          }
          stopListening?.();
          resolve(event);
        });
      });

      try {
        const queued = await uploadFeaturesAsync({
          payload: {
            fileName: layerName.trim(),
            fileType,
            featureIds: selectedFeatureIds,
            ...(userValues.length || groupValues.length ? { permissions: { userValues, groupValues } } : {}),
            properties: {
              ...propertiesToSend,
              folderId: targetFolderId,
            },
          },
        });
        queuedJobId = queued?.jobId;
        const completed = await Promise.race([
          datasetReady,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timed out waiting for saved dataset')), 10 * 60 * 1000)),
        ]);
        if (!completed?.datasetId) throw new Error('Saved dataset did not return a dataset id');
        void queued;
      } finally {
        stopListening?.();
      }

      Notification({
        message: selectedFeatureIds.length > 0 ? 'Selected features saved to file manager' : 'Layer saved to file manager',
        type: NotificationTypes.SUCCESS,
      });
      // #4 — after the file lands in the folder, offer to add it to a map as a
      // layer (default to the current map). "Don't add to a map" = file only.
      setSavedFolderId(targetFolderId);
      setMapChoice(currentMapId != null ? Number(currentMapId) : NO_MAP);
      setStep('map');
    } catch (err: any) {
      // Mutation hooks toast their own errors; just log here.
      console.error('Save Layer As failed:', err);
    }
  };

  const stripHtml = (html?: string) => (html ?? '').replace(/<\/?[^>]+(>|$)/g, '');

  // #4 — optionally add the just-saved dataset to a map as a layer.
  const handleAddToMap = async () => {
    if (mapChoice == null || mapChoice === NO_MAP) {
      setSaveLayerModalVisible(false);
      onSaved?.();
      return;
    }
    try {
      // The dataset/revision was created by the completed save-features job.
      // Save the selected features to the map through the file-manager
      // save-features endpoint, not the S3 save-layer endpoint.
      const featureIds = (layerProps?.selectedFeatureIds ?? []).map(String);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedFeatureIds: _drop, ...propertiesToSend } = omitNonLayerProperties(layerProps) as any;
      await createLayerFromFeaturesAsync({
        payload: {
          mapId: mapChoice,
          layerName: layerName.trim(),
          ...(featureIds.length ? { featureIds } : {}),
          properties: { ...propertiesToSend, folderId: savedFolderId },
        },
      });
      Notification({ message: 'Layer added to map', type: NotificationTypes.SUCCESS });
      setSaveLayerModalVisible(false);
      onSaved?.();
    } catch (err) {
      console.error('Add to map failed:', err);
    }
  };

  const renderMapStep = () => (
    <>
      {renderHeader('Add to a Map?', false)}
      <div className="save-layer-modal__body">
        <p className="save-layer-modal__hint">Saved to the file manager. Optionally add it to a map as a layer.</p>
        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Map Location</label>
          <Select
            value={mapChoice}
            onChange={(v) => setMapChoice(v)}
            style={{ width: '100%' }}
            loading={loadingMaps}
            placeholder="Select a map"
            suffixIcon={<AiFillCaretDown />}
          >
            {allMaps.map((m: any) => {
              const isCurrent = currentMapId != null && Number(m.id) === Number(currentMapId);
              return (
                <Option key={m.id} value={Number(m.id)}>
                  {stripHtml(m.general?.mapTitle ?? String(m.id))}
                  {isCurrent ? ' (Current)' : ''}
                </Option>
              );
            })}
            <Option key="no-map" value={NO_MAP}>
              Don&apos;t add to a map
            </Option>
          </Select>
        </div>
        <div className="save-layer-modal__actions">
          <Button className="save-layer-modal__add-btn" loading={addingToMap} onClick={handleAddToMap}>
            Done
          </Button>
        </div>
      </div>
    </>
  );

  const renderHeader = (title: string, permissionSelector: boolean) => (
    <div className={`save-layer-modal${permissionSelector ? '__permissions' : '__header'}`}>
      <h2 className="save-layer-modal__title">{title}</h2>
      <button className="save-layer-modal__close" onClick={requestClose} aria-label="Close">
        <AiOutlineClose />
      </button>
    </div>
  );

  const renderChoose = () => (
    <>
      {renderHeader('Save Layer As', false)}
      <div className="save-layer-modal__body">
        <div className="save-layer-modal__choices">
          <button className="save-layer-modal__card" onClick={() => setStep('new')}>
            <img src={'/assets/save_new_folder_icon.svg'} />
            <span className="save-layer-modal__card-label">Save to New Folder</span>
          </button>
          <button className="save-layer-modal__card" onClick={() => setStep('existing')}>
            <img src={'/assets/save_existing_folder_icon.svg'} />
            <span className="save-layer-modal__card-label">Save to Existing Folder</span>
          </button>
        </div>
      </div>
    </>
  );

  const renderErrorLine = (key: string) =>
    errors[key] ? (
      <div className="save-layer-modal__error">
        <AiOutlineCloseCircle />
        <span>{errors[key]}</span>
      </div>
    ) : null;

  const permissionsBlock = (
    <>
      <div className="save-layer-modal__field">
        <label className="save-layer-modal__label">Permissions</label>
        <Select
          mode="multiple"
          value={permissionValues}
          onChange={setPermissionValues}
          options={permissionOptions}
          style={{ width: '100%' }}
          showSearch={false}
          suffixIcon={<AiFillCaretDown />}
        />
      </div>
    </>
  );

  const renderNewForm = () => (
    <>
      {renderHeader('Save File to a New Folder', true)}
      <div className="save-layer-modal__body">
        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Location</label>
          <Button
            className={`save-layer-modal__location-btn${errors.location ? ' save-layer-modal__location-btn--error' : ''}`}
            onClick={openFolderSelector}
          >
            <p className="save-layer-modal__location-text">{selectedFolderName || 'Location'}</p>
            <AiFillCaretDown className="save-layer-modal__chevron" />
          </Button>
          {renderErrorLine('location')}
        </div>

        {permissionsBlock}

        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Folder Name</label>
          <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} status={errors.folderName ? 'error' : undefined} />
          {renderErrorLine('folderName')}
        </div>

        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">File Name</label>
          <Input
            value={layerName}
            onChange={(e) => setLayerName(e.target.value)}
            placeholder="e.g. land_use_2026"
            status={errors.fileName ? 'error' : undefined}
          />
          {renderErrorLine('fileName')}
        </div>

        <div className="save-layer-modal__actions">
          <Button className="save-layer-modal__add-btn" loading={isPending} onClick={handleSave}>
            Add
          </Button>
        </div>
      </div>
    </>
  );

  const renderExistingForm = () => (
    <>
      {renderHeader('Save File to Existing Folder', true)}
      <div className="save-layer-modal__body">
        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Folder</label>
          <Button
            className={`save-layer-modal__location-btn${errors.folder ? ' save-layer-modal__location-btn--error' : ''}`}
            onClick={openFolderSelector}
          >
            <p className="save-layer-modal__location-text">{selectedFolderName || 'Folder'}</p>
            <AiFillCaretDown className="save-layer-modal__chevron" />
          </Button>
          {renderErrorLine('folder')}
        </div>

        {permissionsBlock}

        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">File Name</label>
          <Input
            value={layerName}
            onChange={(e) => setLayerName(e.target.value)}
            placeholder="e.g. land_use_2026"
            status={errors.fileName ? 'error' : undefined}
          />
          {renderErrorLine('fileName')}
        </div>

        <div className="save-layer-modal__actions">
          <Button className="save-layer-modal__add-btn" loading={isPending} onClick={handleSave}>
            Add
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Modal
        className="save-layer-modal"
        open={saveLayerModalVisible}
        onCancel={requestClose}
        centered
        maskClosable={false}
        closable={false}
        footer={null}
        width={520}
        destroyOnClose
      >
        {step === 'choose' && renderChoose()}
        {step === 'new' && renderNewForm()}
        {step === 'existing' && renderExistingForm()}
        {step === 'map' && renderMapStep()}
      </Modal>

      <FolderSelectorTree
        open={folderSelectorOpen}
        onClose={closeFolderSelector}
        onSelect={handleFolderSelect}
        variant="light"
        primaryLabel="SELECT"
        initialFolderId={step === 'new' ? locationFolderId : existingFolderId}
      />

      <CustomWarning
        open={confirmCloseOpen}
        onCancel={() => setConfirmCloseOpen(false)}
        onConfirm={forceClose}
        message={
          <>
            Your file will not be saved!
            <br />
            Close the &lsquo;Save As&rsquo; window?
          </>
        }
      />
    </>
  );
};

export default SaveLayerModal;
